import { useMemo } from 'react';

import produce from 'immer';
import { create } from 'zustand';

import { EOverrideAccessRights } from 'api/projectTeamConfigurations/client-projectTeamApi';
import type { WritableDraft } from 'immer/dist/internal';
import toNumber from 'lodash/toNumber';
import toString from 'lodash/toString';
import uniq from 'lodash/uniq';
import {
  GRID_HEADER_ROW_COUNT,
  GRID_TEXT_CELL_MINIMUM_WIDTH
} from 'pages/projects/projectPage/nodeQuery/grid/gridconstants';
import { PENDING_EDITS_STATE } from 'state/pendingEdits/pendingEditsState';
import type { CalculatedSemNode } from 'types/data/CalculatedSemNode';
import type { SemNode } from 'types/data/semNode';
import { devLog } from 'utils/dev-utils';
import { pick } from 'utils/records';
import type { Nullable } from 'utils/values';
import { hasValue } from 'utils/values';
import { calculateNodesAsync } from 'worker/workers';

import type { RequestResultSetUpdateInstruction } from '../data/dataActions';
import type {
  GridSpecification,
  NodeProperties,
  PropertyValue,
  QueryInitialization,
  ScrollPosition
} from '../data/dataState';
import {
  addRowStartAndColumnIndex,
  deepFlattenNodes,
  setOriginalRowIndex
} from '../selectedProject/selectedProjectHooks';
import type { EPropertyTypes } from '../selectedTemplate/selectedTemplateStore';
import { createResultSetEntryId } from './createResultSetEntryId';
import { getResultSetEntry } from './getResultSetEntry';
import { GridActions } from './gridActions';
import type { CapturedSelections } from './utils/cellSelectionPersistence';
import { captureCurrentSelections, restoreCapturedSelections } from './utils/cellSelectionPersistence';
import { deepUpdate } from './utils/deepUpdate';

import 'pages/projects/projectPage/nodeQuery/grid/grid.less';

export interface ColumnFilter {
  // nodeTraversalId!: string;
  keys: string[];
  allowBlanks: EAllowBlanks;
}

export interface PropertyFilter {
  values: PropertyFilterValue[];
  allowBlanks: EAllowBlanks;
  propertyType: EPropertyTypes;
}

export type PropertyFilterValue = {
  selected: boolean;
  id: string;
  value: string;
  humanReadableText: string;
  rawValue: PropertyValue;
};

export enum EAllowBlanks {
  NotSet = 0,
  Allow = 1,
  Disallow = 2
}

export enum EDirection {
  Up = 1,
  Down = 2,
  Left = 3,
  Right = 4
}

export type CompletedResultSets = { [resultSetId: string]: boolean };

export type RawNodes = { [resultSetId: string]: SemNode[] };

export type Filters = { [resultSetId: string]: { [columnKey: string]: ColumnFilter } };
export type PropertyFilters = { [resultSetId: string]: { [columnKey: string]: PropertyFilter } };
export type Sortings = { [resultSetId: string]: number[] };
export type GridSpecifications = { [resultSetId: string]: GridSpecification };

// the selected cell in the node matrix, per user
export type SelectedCellRecord = {
  [userId: string]: { [resultSetId: string]: { [nodeMatrixColumnIndex: number]: number } };
};

export type GridState = {
  copyRequested: { [resultSetId: string]: { [nodeMatrixColumnIndex: number]: number } };
  setCopyRequested: (userName: string) => void;
  clearCopyRequested: () => void;

  pasteRequested: { [resultSetId: string]: { [nodeMatrixColumnIndex: number]: number } };
  setPasteRequested: (userName: string) => void;
  clearPasteRequested: () => void;

  projectId: string;

  rowCounts: { [key: string]: number }; // rowcount per resultset
  nodeMatrixRowCount: { [key: string]: number }; // rowcount per resultset
  nodeMatrix: { [resultSetId: string]: CalculatedSemNode[][] }; // matrix containing the data nodes by column and row

  rootRowMap: { [resultSetId: string]: { [key: number]: number } }; // map for mapping grid row numbers to root row numbers
  rawNodes: RawNodes; // contains the results from the data stream
  rootNodes: { [resultSetId: string]: { [nodeId: string]: CalculatedSemNode } }; // contains the calculated nodes
  completedResultSets: CompletedResultSets; // resultsets that are fully fetched
  scrollPositions: { [resultSetId: string]: ScrollPosition }; // scroll positions per result set
  isInitialized: boolean; // indicates the grid specifications are loaded

  properties: { [nodeId: string]: { [propertyName: string]: PropertyValue } }; // contains all node properties

  gridSpecifications: { [resultSetId: string]: GridSpecification }; // contains all data for rendering results

  filters: Filters; // filters by result set id
  propertyFilters: PropertyFilters; // filters by result set id
  sortings: Sortings; // the number represents the column index. The order of the numbers is the sort order, a negative number means ascending order

  lastActiveGrid?: string;

  addCalculatedNodes: (
    resultSetId: string,
    rawNodes: SemNode[],
    calculatedNodes: CalculatedSemNode[],
    properties: NodeProperties[]
  ) => void; // add nodes that are already calculated internally to the results
  addRootNode: (resultSetId: string, rawNode: SemNode) => Promise<void>; // add a new rootnode to a result set
  updateRootNodesWithoutRecalculation: (resultSetId: string, semNodes: SemNode[]) => void; // update a root node
  removeRootNodesWithoutRecalculation: (resultSetId: string, semNodeIds: string[]) => void; // remove a root node
  replaceRootNodesWithoutRecalculation: (resultSetId: string, semNodes: SemNode[]) => void;
  complete: (resultSetId: string) => void; // mark resultset as complete
  clearIfNotComplete: (resultSetId: string) => void; // remove a resultset if it's not completed
  clear: (resuultSetId: string) => void; // remove a resultset
  recalculateResultSets: (resultSetIds: string[], pendingEditsToClear: string[]) => Promise<void>;

  initializeDataSets2: (projectId: string, queryInitializations: QueryInitialization[]) => void; // temporary method to migrate to backend generated grid specifications
  initializeDataSet: (projectId: string, queryInitialization: QueryInitialization, proposalId?: string) => void;
  initializePanelDataSets: (queryInitializations: QueryInitialization[]) => void;
  saveScrollPosition: (scrollPosition: ScrollPosition, force?: boolean) => void; // update the scrollposition of a grid
  addSingleNodeProperties: (properties: { nodeId: string; properties: { [key: string]: any } }[]) => any;
  addNodeProperties: (nodes: SemNode[]) => void;
  addProperties: (nodeId: string, properties: { [key: string]: any }) => void;
  updateProperties: (
    nodeId: string,
    properties: Record<string, string | number | boolean | null>,
    options: { recalculateCellSizes: boolean }
  ) => Promise<void>; // update or add properties of a grid

  setFilter: (resultSetId: string, columnKey: string, values: string[], allowBlanks: EAllowBlanks) => void;
  setPropertyFilter: (
    resultSetId: string,
    columnKey: string,
    values: PropertyFilterValue[],
    allowBlanks: EAllowBlanks
  ) => void;
  clearFilter: (resultSetId: string, columnKey: string) => void;
  clearFilters: (resultSetId: string) => void;

  sortByColumn: (resultSetId: string, columnIndex: number) => void;
  reverseSort: (resultSetId: string, columnIndex: number) => void;
  clearSort: (resultSetId: string) => void;
  clearColumnSort: (resultSetId: string, columnIndex: number) => void;

  editCells: SelectedCellRecord;
  editCell: (userId: string, resultSetId: string, columnIndex: number, rowIndex: number) => void;
  deEditCell: (userId: string) => void;

  selectedCells: SelectedCellRecord;
  selectCell: (userId: string, resultSetId: string, columnIndex: number, rowIndex: number) => void;

  moveCellSelection: (userId: string, resultSetId: string, direction: EDirection) => void;
  deSelectCell: (userId: string) => void;
  editingNodeProperties: { [userId: string]: { [nodeId: string]: string } }; // user -- nodeId -- property           for syntactic edits
  selectedNodes: { [userId: string]: { [nodeId: string]: string } }; // user -- nodeId -- templateEdgeId     for nodeId for semantic edits
  editNodeProperty: (userId: string, nodeId: string, propertyName: string) => void;
  deEditNodeProperty: (userId: string, nodeId: string, propertyName: string) => void;

  nodeAccessRightsByRoleMap: AccessRightsMap;
  propertyAccessRightsByRoleMap: AccessRightsMap;
  relationAccessRightsByRoleMap: AccessRightsMap;

  setAccessRights: (
    nodeAccessRightsByRoleMap: AccessRightsMap,
    propertyAccessRightsByRoleMap: AccessRightsMap,
    relationAccessRightsByRoleMap: AccessRightsMap
  ) => void;

  dynamicHeaderHeight: { [resultSetId: string]: number };
  setDynamicHeaderHeight: (resultSetId: string, dynamicHeaderHeight: number) => void;

  updateColumnWidth: (resultSetId: string, columnIndex: number, width: number) => void;

  filterColumnIndex: { [resultSetEntryId: string]: number };
  setFilterColumnIndex: (resultSetEntryId: string, filterColumnIndex: number) => void;
  clearFilterColumnIndex: () => void;
};

export type AccessRightsMap = Record<string, Record<string, EOverrideAccessRights>>;
export type SimpleAccessRightsMap = { [key: string]: boolean }; // object key: isDisabled

export const GRID_STORE = create<GridState>((set, getState) => ({
  projectId: '',
  completedResultSets: {},
  rootNodes: {},
  nodeMatrix: {},
  selectedCells: {},
  editCells: {},
  isInitialized: false,
  rawNodes: {},
  rootRowMap: {},
  scrollPositions: {},
  gridSpecifications: {},
  rowCounts: {},
  nodeMatrixRowCount: {},
  properties: {},
  filters: {},
  propertyFilters: {},
  sortings: {},
  dynamicHeaderHeight: {},

  setDynamicHeaderHeight: (resultSetId: string, dynamicHeaderHeight: number) =>
    set(({ dynamicHeaderHeight: originalDynamicHeaderHeight }) => {
      const updatedDynamicHeaderHeight = produce(originalDynamicHeaderHeight, (draft) => {
        draft[resultSetId] = dynamicHeaderHeight;
      });

      return { dynamicHeaderHeight: updatedDynamicHeaderHeight };
    }),

  copyRequested: {},
  setCopyRequested: (userName: string) =>
    set(({ selectedCells, copyRequested: originalCopyRequested }) => {
      const copyRequested = selectedCells[userName];

      return { copyRequested: copyRequested || originalCopyRequested };
    }),

  clearCopyRequested: () => set({ copyRequested: {} }),

  pasteRequested: {},
  setPasteRequested: (userName: string) =>
    set(({ selectedCells, pasteRequested: originalPasteRequested }) => {
      return { pasteRequested: selectedCells[userName] || originalPasteRequested };
    }),

  clearPasteRequested: () => set({ pasteRequested: {} }),

  addCalculatedNodes: (
    resultSetId: string,
    rawNodes: SemNode[],
    calculatedNodes: CalculatedSemNode[],
    properties: NodeProperties[]
  ) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.rawNodes[resultSetId].push(...rawNodes);

        properties.forEach((p) => {
          draft.properties[p.nodeId] = { ...draft.properties[p.nodeId], ...p.properties };
        });

        calculatedNodes.forEach((c) => {
          draft.rootNodes[resultSetId][c.id] = c;
        });

        const rootRowKeys = Object.keys(state.rootNodes[resultSetId]);
        const numberOfRootRows = rootRowKeys.length;

        const renderedRowStart =
          numberOfRootRows === 0
            ? GRID_HEADER_ROW_COUNT
            : state.rootNodes[resultSetId][rootRowKeys[rootRowKeys.length - 1]].rowStart +
              state.rootNodes[resultSetId][rootRowKeys[rootRowKeys.length - 1]].lineCount;
        const rowIndexedNodes = addRowStartAndColumnIndex(
          calculatedNodes,
          renderedRowStart,
          state.gridSpecifications[resultSetId].columnIndexByKey,
          state.gridSpecifications[resultSetId].edgeKeyMap,
          'root'
        );
        const rowNumber =
          numberOfRootRows === 0
            ? GRID_HEADER_ROW_COUNT
            : state.rootNodes[resultSetId][rootRowKeys[rootRowKeys.length - 1]].rowNumber + 1;
        const indexedCalculatedNewNodes = setOriginalRowIndex(
          rowIndexedNodes,
          rowNumber,
          state.gridSpecifications[resultSetId].edgeKeyMap,
          'root'
        );

        const flatResult = deepFlattenNodes(indexedCalculatedNewNodes);

        flatResult.forEach((item) => {
          draft.nodeMatrix[resultSetId][item.column][item.rowStart] = item;
        });

        indexedCalculatedNewNodes.forEach((i) => {
          draft.rootNodes[resultSetId][i.id] = i;
        });

        const rowsPerRootNode = indexedCalculatedNewNodes.reduce<{ rowStart: number; rowEnd: number }[]>(
          (previous, node) => {
            const previousRowNumber = previous.length === 0 ? node.rowStart : previous[previous.length - 1].rowEnd;
            previous.push({ rowStart: previousRowNumber, rowEnd: previousRowNumber + node.lineCount });
            return previous;
          },
          []
        );

        let lastRowCount = 0;
        let lastNodeMatrixRowCount = 0;

        rowsPerRootNode.reduce<Record<number, number>>((prev, current, index) => {
          const start = current.rowStart;
          const end = current.rowEnd - 1;
          const rowsForThisNode = Array.from({ length: end - start + 1 }, (_, index) => start + index);
          rowsForThisNode.forEach((rowIndex) => {
            prev[rowIndex] = index + rowNumber;

            draft.rootRowMap[resultSetId][rowIndex] = index + rowNumber; // <-- draft changed here

            lastRowCount = index + rowNumber;
            lastNodeMatrixRowCount = current.rowEnd;
          });

          return prev;
        }, {});

        draft.rowCounts[resultSetId] = lastRowCount;
        draft.nodeMatrixRowCount[resultSetId] = lastNodeMatrixRowCount;
      });

      return newState;
    }),

  addRootNode: async (resultSetId: string, rawNode: SemNode) => {
    set((state) =>
      produce(state, (draft) => {
        addRawNodeToDraft(draft, resultSetId, rawNode);
      })
    );

    await recalculateResultSetsAndCommit(getState, set, [resultSetId]);
  },

  updateRootNodesWithoutRecalculation: (resultSetId: string, semNodes: SemNode[]) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        semNodes.forEach((semNode) => replaceNodeInRawNodesAndUpdatePropertyValuesInDraft(draft, resultSetId, semNode));
      });

      return newState;
    }),
  removeRootNodesWithoutRecalculation: (resultSetId: string, semNodeIds: string[]) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        semNodeIds.forEach((nodeId) => {
          removeNodeInRawNodes(state, draft, resultSetId, nodeId);
        });
      });

      return newState;
    }),

  replaceRootNodesWithoutRecalculation: (resultSetId: string, semNodes: SemNode[]) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        //draft.rawNodes[resultSetId].splice(0,draft.rawNodes[resultSetId].length);
        draft.rawNodes[resultSetId] = [...semNodes];
      });

      return newState;
    }),

  complete: (resultSetId: string) => {
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.completedResultSets[resultSetId] = true;
      });

      return newState;
    });

    if (resultSetsToLayOutOnceLoaded.delete(resultSetId)) {
      recalculateResultSetsAndCommit(getState, set, [resultSetId]).catch((error: unknown) => {
        console.error('Error laying out a grid after it loaded:', error);
      });
    }
  },

  clearIfNotComplete: (resultSetId: string) => {
    if (getState().completedResultSets[resultSetId] !== true) {
      cancelInFlightRecalculations([resultSetId]);
    }

    set((state) => {
      const newState = produce(state, (draft) => {
        if (state.completedResultSets[resultSetId] !== true) {
          removeResultSet(draft, resultSetId);
        }
      });

      return newState;
    });
  },

  clear: (resultSetId: string) => {
    cancelInFlightRecalculations([resultSetId]);

    set((state) => {
      const newState = produce(state, (draft) => {
        removeResultSet(draft, resultSetId);
      });

      return newState;
    });
  },

  recalculateResultSets: async (resultSetIds: string[], pendingEditsToClear: string[]) => {
    await recalculateResultSetsAndCommit(getState, set, resultSetIds);

    PENDING_EDITS_STATE.getState().processPendingEdits(pendingEditsToClear);
  },
  // TODO: this is no longer used, need to remove use in test cases before removing here
  initializeDataSets2: (projectId: string, queryInitializations: QueryInitialization[]) => {
    const nodeMatrix: Record<string, CalculatedSemNode[][]> = {};
    const rowCounts: Record<string, number> = {};
    const nodeMatrixRowCount: Record<string, number> = {};
    const rawNodes: Record<string, SemNode[]> = {};
    const rootRowMap: Record<string, Record<number, number>> = {};
    const rootNodes: Record<string, { [nodeId: string]: CalculatedSemNode }> = {};
    const scrollPositions: Record<string, ScrollPosition> = {};
    const gridSpecifications: Record<string, GridSpecification> = {};
    const filters: Record<string, Record<string, ColumnFilter>> = {};
    const propertyFilters: PropertyFilters = {};
    const sortings: Sortings = {};
    const completedResultSets: CompletedResultSets = {};

    for (const queryInitialization of queryInitializations) {
      const { resultSetId, specification, gridSpecification } = queryInitialization;

      gridSpecifications[resultSetId] = gridSpecification;
      nodeMatrix[resultSetId] = queryInitialization.nodeMatrix;
      rowCounts[resultSetId] = 0;
      nodeMatrixRowCount[resultSetId] = 0;
      rawNodes[resultSetId] = [];
      rootRowMap[resultSetId] = {};
      rootNodes[resultSetId] = {};
      sortings[resultSetId] = [];
      completedResultSets[resultSetId] = false;

      scrollPositions[resultSetId] = { gridPositionStart: 0, gridPositionEnd: 0, resultSetId };

      filters[resultSetId] = Object.keys(specification.columns).reduce<Record<string, ColumnFilter>>(
        (prev2, columnKey) => {
          if (hasValue(gridSpecification.propertyKeyMap[columnKey])) {
            prev2[columnKey] = { keys: [], allowBlanks: EAllowBlanks.NotSet };
          }
          return prev2;
        },
        {}
      );

      propertyFilters[resultSetId] = Object.entries(specification.columns).reduce<Record<string, PropertyFilter>>(
        (prev2, [columnKey, targetColumn]) => {
          if (!targetColumn.isSemanticEdit && targetColumn.propertyTraversal) {
            const propertyType = targetColumn.propertyTraversal.propertyType;
            prev2[columnKey] = { values: [], allowBlanks: EAllowBlanks.NotSet, propertyType };
          }
          return prev2;
        },
        {}
      );
    }

    set({
      isInitialized: true,
      projectId,
      nodeMatrix,
      rowCounts,
      nodeMatrixRowCount,
      rawNodes,
      rootRowMap,
      rootNodes,
      scrollPositions,
      gridSpecifications,
      filters,
      propertyFilters,
      sortings,
      completedResultSets
    });
  },
  initializeDataSet: (projectId: string, queryInitialization: QueryInitialization, proposalId?: string) => {
    const { specification, gridSpecification } = queryInitialization;
    const { queryId, dataNodeId, dynamicId } = getResultSetEntry(queryInitialization.resultSetId);
    const resultSetId = createResultSetEntryId(projectId, proposalId, queryId, dataNodeId, dynamicId);

    cancelInFlightRecalculations([resultSetId]);

    const queryFilters = Object.keys(specification.columns).reduce<Record<string, ColumnFilter>>((prev2, columnKey) => {
      if (hasValue(gridSpecification.propertyKeyMap[columnKey])) {
        prev2[columnKey] = { keys: [], allowBlanks: EAllowBlanks.NotSet };
      }
      return prev2;
    }, {});

    const queryPropertyFilters = Object.entries(specification.columns).reduce<Record<string, PropertyFilter>>(
      (prev2, [columnKey, targetColumn]) => {
        if (!targetColumn.isSemanticEdit && targetColumn.propertyTraversal) {
          const propertyType = targetColumn.propertyTraversal.propertyType;
          prev2[columnKey] = { values: [], allowBlanks: EAllowBlanks.NotSet, propertyType };
        }
        return prev2;
      },
      {}
    );

    set((state) => ({
      ...state,
      isInitialized: true,
      projectId,
      nodeMatrix: { ...state.nodeMatrix, [resultSetId]: queryInitialization.nodeMatrix },
      rowCounts: { ...state.rowCounts, [resultSetId]: 0 },
      nodeMatrixRowCount: { ...state.nodeMatrixRowCount, [resultSetId]: 0 },
      rawNodes: { ...state.rawNodes, [resultSetId]: [] },
      rootRowMap: { ...state.rootRowMap, [resultSetId]: {} },
      rootNodes: { ...state.rootNodes, [resultSetId]: {} },
      scrollPositions: {
        ...state.scrollPositions,
        [resultSetId]: { gridPositionStart: 0, gridPositionEnd: 0, resultSetId }
      },
      gridSpecifications: { ...state.gridSpecifications, [resultSetId]: gridSpecification },
      filters: { ...state.filters, [resultSetId]: queryFilters },
      propertyFilters: { ...state.propertyFilters, [resultSetId]: queryPropertyFilters },
      sortings: { ...state.sortings, [resultSetId]: [] },
      completedResultSets: { ...state.completedResultSets, [resultSetId]: false }
    }));
  },
  initializePanelDataSets: (queryInitializations: QueryInitialization[]) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        queryInitializations.forEach((queryInitialization) => {
          const resultSetId = queryInitialization.resultSetId;
          if (state.rawNodes[resultSetId] === undefined) {
            const zeroScrollPositions = {
              resultSetId: resultSetId,
              gridPositionStart: 0,
              gridPositionEnd: 0
            } as ScrollPosition;
            draft.rawNodes[resultSetId] = [];

            draft.gridSpecifications[resultSetId] = queryInitialization.gridSpecification;
            draft.nodeMatrix[resultSetId] = queryInitialization.nodeMatrix;
            draft.scrollPositions[zeroScrollPositions.resultSetId] = zeroScrollPositions;
            draft.rootNodes[resultSetId] = {};
            draft.rootRowMap[resultSetId] = {};
            draft.rowCounts[resultSetId] = 0;
            draft.nodeMatrixRowCount[resultSetId] = 0;
            draft.completedResultSets[resultSetId] = false;

            // TODO filters
            draft.filters[resultSetId] = createFilterFromQueryInitialization(queryInitialization);
            draft.propertyFilters[resultSetId] = createPropertyFilterFromQueryInitialization(queryInitialization);
            draft.sortings[resultSetId] = [];
          } else {
            devLog('already initialized');
          }
        });
      });

      return newState;
    }),

  saveScrollPosition: ({ resultSetId, gridPositionStart, gridPositionEnd }) => {
    set(({ scrollPositions }) => {
      const updatedScrollPositions = produce(scrollPositions, (draft) => {
        if (hasValue(gridPositionStart)) {
          draft[resultSetId].gridPositionStart = gridPositionStart;
        }
        if (hasValue(gridPositionEnd)) {
          draft[resultSetId].gridPositionEnd = gridPositionEnd;
        }
      });

      return { scrollPositions: updatedScrollPositions };
    });
  },
  addSingleNodeProperties: (
    nodeWithProperties: { nodeId: string; properties: { [propertyName: string]: PropertyValue } }[]
  ) =>
    set(({ properties }) => {
      const mergedProperties = produce(properties, (draft) => {
        // Merge existing properties with new ones:
        // - Keep existing properties (if any)
        // - Add/Update with new properties
        nodeWithProperties.forEach(({ nodeId, properties }) => {
          draft[nodeId] = {
            ...(draft[nodeId] || {}),
            ...properties
          };
        });
      });
      return { properties: mergedProperties };
    }),
  addNodeProperties: (semNodes: SemNode[]) =>
    set((state) => {
      const uniqueNodeIds = uniq(semNodes.map((n) => n.Id));
      const unifiedNodes: SemNode[] = uniqueNodeIds.map((n) => {
        const nodesWithId = semNodes.filter((s) => s.Id === n);

        const allProperties = uniq(
          nodesWithId.flatMap((n) =>
            Object.keys(n.Properties).map((propertyKey) => {
              return { key: propertyKey, value: n.Properties[propertyKey] };
            })
          )
        );

        return {
          ...nodesWithId[0],
          Properties: allProperties.reduce<Record<string, PropertyValue>>((prev, curr) => {
            if (prev[curr.key] === undefined) {
              return { ...prev, [curr.key]: curr.value };
            } else {
              return { ...prev };
            }
          }, {})
        };
      });

      const newState = produce(state, (draft) => {
        unifiedNodes.forEach((n) => {
          const existing = state.properties[n.Id];
          if (existing === undefined) {
            const newProperties = Object.keys(n.Properties).reduce((prev, key) => {
              const propertyValue = toString(n.Properties[key]);

              return {
                ...prev,
                [key]: propertyValue
              };
            }, {});
            draft.properties[n.Id] = newProperties;
          } else {
            const newProperties = uniq([...Object.keys(existing), ...Object.keys(n.Properties)]).reduce(
              (prev, curr) => {
                if (n.Properties[curr] !== undefined) {
                  const propertyValue = toString(n.Properties[curr]);
                  return {
                    ...prev,
                    [curr]: propertyValue
                  };
                } else {
                  return { ...prev, [curr]: existing[curr] };
                }
              },
              {} as { [propertyName: string]: PropertyValue }
            );
            draft.properties[n.Id] = newProperties;
          }
        });
      });

      return newState;
    }),

  addProperties: (nodeId: string, properties: { [key: string]: any }) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const newProperties = Object.keys(properties).reduce((prev, key) => {
          const propertyValue = toString(properties[key]);

          return {
            ...prev,
            [key]: propertyValue
          };
        }, {});

        draft.properties[nodeId] = newProperties;
      });

      return newState;
    }),

  updateProperties: async (
    nodeId: string,
    properties: SemNode['Properties'],
    options: { recalculateCellSizes: boolean } = {
      recalculateCellSizes: true
    }
  ) => {
    const state = getState();

    const resultSetIds = getResultSetsToUpdate(
      state.rawNodes,
      state.completedResultSets,
      state.filters,
      state.propertyFilters,
      state.sortings,
      nodeId,
      []
    ).map(({ resultSetId }) => resultSetId);

    set((current) =>
      produce(current, (draft) => {
        draft.properties[nodeId] = { ...draft.properties[nodeId], ...properties };

        for (const resultSetId of resultSetIds) {
          deepUpdate(draft.rawNodes[resultSetId], nodeId, properties);
        }
      })
    );

    if (options.recalculateCellSizes) {
      await recalculateResultSetsAndCommit(getState, set, resultSetIds);
    }
  },

  setFilter: (resultSetId: string, columnKey: string, values: string[]) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const newLocal = draft.filters[resultSetId][columnKey];
        newLocal.keys = [...values];
      });

      return newState;
    }),
  setPropertyFilter: (resultSetId: string, columnKey: string, values: PropertyFilterValue[]) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const newLocal = draft.propertyFilters[resultSetId][columnKey];
        newLocal.values = [...values];
      });

      return newState;
    }),

  clearFilter: (resultSetId: string, columnKey: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.filters[resultSetId][columnKey].keys = [];
        draft.propertyFilters[resultSetId][columnKey].values = [];
      });

      return newState;
    }),

  clearFilters: (resultSetId: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        Object.keys(state.filters[resultSetId]).forEach((columnKey) => {
          draft.filters[resultSetId][columnKey].keys = [];
        });

        Object.keys(state.propertyFilters[resultSetId]).forEach((columnKey) => {
          draft.propertyFilters[resultSetId][columnKey].values = [];
        });
      });

      return newState;
    }),

  sortByColumn: (resultSetId: string, columnIndex: number) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const currentAscIndex = draft.sortings[resultSetId].indexOf(columnIndex);
        const currentDescIndex = draft.sortings[resultSetId].indexOf(columnIndex * -1);

        if (currentDescIndex > -1) {
          draft.sortings[resultSetId][currentDescIndex] = columnIndex;
        } else if (currentAscIndex === -1) {
          draft.sortings[resultSetId].push(columnIndex);
        }
      });

      return newState;
    }),

  reverseSort: (resultSetId: string, columnIndex: number) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const currentAscIndex = draft.sortings[resultSetId].indexOf(columnIndex);
        const currentDescIndex = draft.sortings[resultSetId].indexOf(columnIndex * -1);

        if (currentAscIndex > -1) {
          draft.sortings[resultSetId][currentAscIndex] = columnIndex * -1;
        } else if (currentDescIndex === -1) {
          draft.sortings[resultSetId].push(columnIndex * -1);
        }
      });

      return newState;
    }),
  clearSort: (resultSetId: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.sortings[resultSetId] = [];
      });

      return newState;
    }),

  clearColumnSort: (resultSetId: string, columnIndex: number) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const sortings = draft.sortings[resultSetId];
        let currentIndex = sortings.indexOf(columnIndex);

        if (sortings.length === 1) {
          draft.sortings[resultSetId] = [];
        } else {
          if (currentIndex === -1) {
            currentIndex = sortings.indexOf(columnIndex * -1);
          }
          if (currentIndex !== -1) {
            draft.sortings[resultSetId].splice(currentIndex, 1);
          }
        }
      });

      return newState;
    }),

  editCell: (userId: string, resultSetId: string, columnIndex: number, rowIndex: number) =>
    set(({ editCells }) => {
      const updatedEditCells = produce(editCells, (draft) => {
        draft[userId] = { [resultSetId]: { [columnIndex]: rowIndex } };
      });

      return { editCells: updatedEditCells, lastActiveGrid: resultSetId };
    }),

  deEditCell: (userId: string) =>
    set((state) => {
      // Do not trigger an update if the user is not editing
      if (!hasValue(state.editCells[userId]) && !hasValue(state.editingNodeProperties[userId])) {
        return state;
      }

      const updatedEditCells = produce(state.editCells, (draft) => {
        delete draft[userId];
      });

      const updatedEditingNodeProperties = produce(state.editingNodeProperties, (draft) => {
        delete draft[userId];
      });

      return { editCells: updatedEditCells, editingNodeProperties: updatedEditingNodeProperties };
    }),

  selectCell: (userId: string, resultSetId: string, columnIndex: number, rowIndex: number) =>
    set(({ selectedCells }) => {
      return {
        selectedCells: produce(selectedCells, (draft) => {
          draft[userId] = { [resultSetId]: { [columnIndex]: rowIndex } };
        })
      };
    }),

  moveCellSelection: (userId: string, resultSetId: string, direction: EDirection) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const currentSelection = state.selectedCells[userId]?.[resultSetId];

        if (currentSelection !== undefined) {
          const currentColumn = toNumber(Object.keys(currentSelection)[0]);
          const currentRow = currentSelection[currentColumn];
          const dataColumn = state.gridSpecifications[resultSetId].nodeMatrixColumnIndexMap[currentColumn];

          let nextSelectedCell: Nullable<[columnIndex: number, rowIndex: number]> = null;

          if (direction === EDirection.Up || direction === EDirection.Down) {
            let hasNextPosition = false;
            let moveDownSteps = direction === EDirection.Down ? 1 : -1;
            let iterations = 0;
            while (!hasNextPosition) {
              const nextPosition = state.nodeMatrix[resultSetId][dataColumn][currentRow + moveDownSteps];
              if (nextPosition !== undefined) {
                hasNextPosition = true;
              } else {
                moveDownSteps = moveDownSteps + (direction === EDirection.Down ? 1 : -1);
              }

              // MAX 50 iterations
              if (iterations > 50) {
                hasNextPosition = true;
              }

              iterations = iterations + 1;
            }

            if (iterations < 50) {
              nextSelectedCell = [currentColumn, currentRow + moveDownSteps];
            }
          } else {
            let isDoneDeterminingPosition = false;
            let moveRightSteps = direction === EDirection.Right ? 1 : -1;
            let iterations = 0;
            let nextColumn = currentColumn;
            let nextRow = currentRow;

            while (!isDoneDeterminingPosition) {
              // see if the next column exists
              const nextColumnExists =
                state.gridSpecifications[resultSetId].nodeMatrixColumnIndexMap[currentColumn + moveRightSteps] !==
                  undefined && currentColumn + moveRightSteps > 0;

              if (!nextColumnExists) {
                // if not, just stop
                isDoneDeterminingPosition = true;
              } else {
                const nextDataColumn =
                  state.gridSpecifications[resultSetId].nodeMatrixColumnIndexMap[currentColumn + moveRightSteps];

                // check if there is a node at that position
                const nextNode = state.nodeMatrix[resultSetId][nextDataColumn][currentRow];

                if (nextNode !== undefined) {
                  isDoneDeterminingPosition = true;
                  nextColumn = currentColumn + moveRightSteps;
                } else {
                  let moveUpSteps = -1;
                  let isDoneMovingUp;
                  let moveUpIterations = 0;
                  while (!isDoneMovingUp) {
                    const nextNode = state.nodeMatrix[resultSetId][nextDataColumn][currentRow + moveUpSteps];

                    if (nextNode !== undefined) {
                      isDoneMovingUp = true;
                      isDoneDeterminingPosition = true;
                      nextColumn = currentColumn + moveRightSteps;
                      nextRow = currentRow + moveUpSteps;
                    } else {
                      if (moveUpIterations > 50) {
                        isDoneMovingUp = true;
                      }

                      moveUpIterations = moveUpIterations + 1;
                      moveUpSteps = moveUpSteps - 1;
                    }
                  }
                }

                moveRightSteps = moveRightSteps + (direction === EDirection.Right ? 1 : -1);
              }

              // it exists, check what column in the node matrix contains data nodes

              if (iterations > 50) {
                isDoneDeterminingPosition = true;
              }

              iterations = iterations + 1;
            }

            if (iterations < 50) {
              nextSelectedCell = [nextColumn, nextRow];
            }
          }

          if (hasValue(nextSelectedCell)) {
            const [columnIndex, rowIndex] = nextSelectedCell;
            draft.selectedCells[userId][resultSetId] = { [columnIndex]: rowIndex };
          }
        }
      });

      return newState;
    }),

  deSelectCell: (userId: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        if (draft.selectedCells[userId] !== undefined) {
          delete draft.selectedCells[userId];
        }
      });

      return newState;
    }),
  editingNodeProperties: {},
  selectedNodes: {},
  editNodeProperty: (userId: string, nodeId: string, propertyName: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const selectedCell = { [nodeId]: propertyName };
        draft.editingNodeProperties[userId] = selectedCell;
        if (!draft.selectedNodes[userId] === undefined) {
          delete draft.selectedNodes[userId];
        }
      });

      return newState;
    }),
  deEditNodeProperty: (userId: string, nodeId: string, propertyName: string) =>
    set((state) => {
      const current = state.editingNodeProperties[userId];
      if (!hasValue(current) || current[nodeId] !== propertyName) {
        return state;
      }

      const updatedEditingNodeProperties = produce(state.editingNodeProperties, (draft) => {
        delete draft[userId];
      });

      return { editingNodeProperties: updatedEditingNodeProperties };
    }),

  nodeAccessRightsByRoleMap: {},
  propertyAccessRightsByRoleMap: {},
  relationAccessRightsByRoleMap: {},

  setAccessRights: (
    nodeAccessRightsByRoleMap: AccessRightsMap,
    propertyAccessRightsByRoleMap: AccessRightsMap,
    relationAccessRightsByRoleMap: AccessRightsMap
  ) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.nodeAccessRightsByRoleMap = nodeAccessRightsByRoleMap;
        draft.propertyAccessRightsByRoleMap = propertyAccessRightsByRoleMap;
        draft.relationAccessRightsByRoleMap = relationAccessRightsByRoleMap;
      });

      return newState;
    }),

  updateColumnWidth: (resultSetId: string, columnIndex: number, widthDifference: number) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const key = Object.keys(state.gridSpecifications[resultSetId].columnIndexByKey).find(
          (key) => state.gridSpecifications[resultSetId].columnIndexByKey[key] === columnIndex
        )!;
        const oldWidth = state.gridSpecifications[resultSetId].columnWidthsByKey[key];

        if (oldWidth + widthDifference > GRID_TEXT_CELL_MINIMUM_WIDTH) {
          draft.gridSpecifications[resultSetId].columnWidthsByKey[key] += widthDifference;
          draft.gridSpecifications[resultSetId].columnSpecificationByIndexMap[columnIndex].width += widthDifference;
          draft.gridSpecifications[resultSetId].columnWidthsByIndex[columnIndex] += widthDifference;
        }
      });

      return newState;
    }),
  filterColumnIndex: {},
  setFilterColumnIndex: (resultSetEntryId, filterColumnIndex) =>
    set({ filterColumnIndex: { [resultSetEntryId]: filterColumnIndex } }),
  clearFilterColumnIndex: () => set({ filterColumnIndex: {} })
}));

export const useGridActions = () => {
  return useMemo(() => new GridActions(), []);
};

export function GetPossibleNewRoots(
  originatingResultSetId: string,
  queryNodeId: string,
  connectedNodeIds: string[],
  rawNodes: RawNodes,
  completedResultSets: CompletedResultSets,
  gridSpecifications: GridSpecifications,
  filters: Filters,
  propertyFilters: PropertyFilters,
  sortings: Sortings,
  parentNodeId?: string
): RequestResultSetUpdateInstruction[] {
  const { projectId, proposalId, queryId, dataNodeId } = getResultSetEntry(originatingResultSetId);
  const normalizedResultSetId = createResultSetEntryId(projectId, proposalId, queryId);

  const columnSpecificationByIndexMap = gridSpecifications[normalizedResultSetId]?.columnSpecificationByIndexMap;
  const targetColumnSpecification = hasValue(columnSpecificationByIndexMap)
    ? Object.values(columnSpecificationByIndexMap).find((spec) => spec.nodeTraversal.id === queryNodeId)
    : undefined;

  if (!targetColumnSpecification) {
    return [];
  }

  const resultSetUpdateInstructions: RequestResultSetUpdateInstruction[] = hasValue(dataNodeId)
    ? Object.keys(rawNodes)
        .filter((resultSetId) => {
          const isComplete = completedResultSets[resultSetId] === true;
          const { queryId: queryId2, dataNodeId: dataNodeId2 } = getResultSetEntry(resultSetId);
          const targetsSameNode = dataNodeId2 === dataNodeId && queryId2 !== queryId;
          return isComplete && targetsSameNode;
        })
        .map((resultSetId) => {
          const { projectId, queryId: queryId3, dataNodeId: dataNodeId3 } = getResultSetEntry(resultSetId);
          return {
            projectId,
            proposalId,
            queryId: queryId3,
            resultSetId,
            filters: filters[resultSetId] || {},
            propertyFilters: propertyFilters[resultSetId] || {},
            rootNodesToRefresh: dataNodeId3 ? [dataNodeId3] : [],
            sortings: sortings[resultSetId] || []
          };
        })
    : [];

  // for complete resultset raw nodes
  findAndAddPossibleUpdates(connectedNodeIds, targetColumnSpecification.nodeTraversal.targetTemplateNodeId);
  if (parentNodeId && targetColumnSpecification.parentColumnId) {
    const parentTargetTemplateNode =
      gridSpecifications[normalizedResultSetId].columnSpecificationByIndexMap[
        gridSpecifications[normalizedResultSetId].columnIndexByKey[targetColumnSpecification.parentColumnId]
      ].nodeTraversal;
    findAndAddPossibleUpdates([parentNodeId], parentTargetTemplateNode.targetTemplateNodeId);
  }

  return resultSetUpdateInstructions;

  function findAndAddPossibleUpdates(nodesToFind: string[], targetTemplateNodeId: string) {
    Object.keys(rawNodes)
      .filter((resultSetId) => completedResultSets[resultSetId])
      .forEach((resultSetId) => {
        const rootNodeTargetsSameTemplateNode =
          gridSpecifications[resultSetId].columnSpecificationByIndexMap[0].nodeTraversal.targetTemplateNodeId ===
          targetTemplateNodeId;
        if (rootNodeTargetsSameTemplateNode) {
          const { projectId, proposalId, queryId, dataNodeId } = getResultSetEntry(resultSetId);
          if (dataNodeId === undefined) {
            const missingRootNodes = nodesToFind.filter(
              (f) => rawNodes[resultSetId].find((rn) => rn.Id === f) === undefined
            );
            if (missingRootNodes.length > 0) {
              resultSetUpdateInstructions.push({
                filters: filters[resultSetId],
                propertyFilters: propertyFilters[resultSetId],
                projectId: projectId,
                proposalId: proposalId,
                queryId: queryId,
                resultSetId: resultSetId,
                rootNodesToRefresh: missingRootNodes,
                sortings: sortings[resultSetId]
              });
            }
          } else {
            resultSetUpdateInstructions.push({
              filters: filters[resultSetId],
              propertyFilters: propertyFilters[resultSetId],
              projectId: projectId,
              proposalId: proposalId,
              queryId: queryId,
              resultSetId: resultSetId,
              rootNodesToRefresh: [dataNodeId],
              sortings: sortings[resultSetId]
            });
          }
        }
      });
  }
}

export function getResultSetsToUpdate(
  rawNodes: RawNodes,
  completedResultSets: CompletedResultSets,
  filters: Filters,
  propertyFilters: PropertyFilters,
  sortings: Sortings,
  updatedParentNodeId: string,
  instructionToIgnores: RequestResultSetUpdateInstruction[]
) {
  const resultSetUpdateInstructions: RequestResultSetUpdateInstruction[] = [];

  const resultSetIds = Object.keys(rawNodes).filter((r) => completedResultSets[r]);

  resultSetIds.forEach((rawNodesResultSetId) => {
    const { queryId, projectId, proposalId } = getResultSetEntry(rawNodesResultSetId);
    rawNodes[rawNodesResultSetId].forEach((rawRootNode) => {
      const allNodesInRootNode = flattenSemNode(rawRootNode);

      if (allNodesInRootNode.find((fn) => fn.Id === updatedParentNodeId) !== undefined) {
        instructionToIgnores.forEach((instructionToIgnore) => {
          if (
            instructionToIgnore !== undefined &&
            rawNodesResultSetId === instructionToIgnore.resultSetId &&
            instructionToIgnore.rootNodesToRefresh.find((rtr) => rtr === rawRootNode.Id)
          ) {
            // console.log('skipping')
          } else {
            const updateSet = resultSetUpdateInstructions.find((rtu) => rtu.resultSetId === rawNodesResultSetId);

            if (updateSet === undefined) {
              resultSetUpdateInstructions.push({
                resultSetId: rawNodesResultSetId,
                rootNodesToRefresh: [rawRootNode.Id],
                filters: filters[rawNodesResultSetId],
                propertyFilters: propertyFilters[rawNodesResultSetId],
                sortings: sortings[rawNodesResultSetId],
                queryId: queryId,
                projectId: projectId,
                proposalId: proposalId
              });
            } else {
              updateSet.rootNodesToRefresh.push(rawRootNode.Id);
            }
          }
        });

        if (instructionToIgnores.length === 0) {
          const updateSet = resultSetUpdateInstructions.find((rtu) => rtu.resultSetId === rawNodesResultSetId);
          if (updateSet === undefined) {
            resultSetUpdateInstructions.push({
              resultSetId: rawNodesResultSetId,
              rootNodesToRefresh: [rawRootNode.Id],
              filters: filters[rawNodesResultSetId],
              propertyFilters: propertyFilters[rawNodesResultSetId],
              sortings: sortings[rawNodesResultSetId],
              queryId: queryId,
              projectId: projectId,
              proposalId: proposalId
            });
          } else {
            updateSet.rootNodesToRefresh.push(rawRootNode.Id);
          }
        }
      }
    });
  });

  // Refresh detail grids whose dataNodeId matches the updated parent.
  // Detail grids store children in rawNodes, not the parent itself,
  // so the flattenSemNode scan above won't find them.
  resultSetIds.forEach((rawNodesResultSetId) => {
    const { queryId, projectId, proposalId, dataNodeId } = getResultSetEntry(rawNodesResultSetId);
    if (dataNodeId === updatedParentNodeId) {
      const alreadyCovered = resultSetUpdateInstructions.some(
        (instruction) => instruction.resultSetId === rawNodesResultSetId
      );
      const shouldIgnore = instructionToIgnores.some((instruction) => instruction.resultSetId === rawNodesResultSetId);

      if (!alreadyCovered && !shouldIgnore) {
        resultSetUpdateInstructions.push({
          resultSetId: rawNodesResultSetId,
          rootNodesToRefresh: [dataNodeId],
          filters: filters[rawNodesResultSetId],
          propertyFilters: propertyFilters[rawNodesResultSetId],
          sortings: sortings[rawNodesResultSetId],
          queryId,
          projectId,
          proposalId
        });
      }
    }
  });

  return resultSetUpdateInstructions;
}

/// DRAFT ACTIONS

function removeResultSet(draft: WritableDraft<GridState>, resultSetId: string) {
  draft.completedResultSets[resultSetId] = false;

  try {
    Object.keys(draft.nodeMatrix[resultSetId]).forEach((key) => {
      const columnToClear = toNumber(key);
      draft.nodeMatrix[resultSetId][columnToClear] = [];
    });
  } catch (error) {
    console.error('Error clearing result set');
  }

  draft.rawNodes[resultSetId] = [];
  draft.rootNodes[resultSetId] = {};
  draft.rootRowMap[resultSetId] = {};
  draft.rowCounts[resultSetId] = 0;
  draft.nodeMatrixRowCount[resultSetId] = 0;
  // Intentionally skipping scrollPositions as we want to always keep the scroll position
}

function addRawNodeToDraft(draft: WritableDraft<GridState>, resultSetId: string, nodeToAdd: SemNode) {
  if (draft.rawNodes[resultSetId] !== undefined) {
    draft.rawNodes[resultSetId].push(nodeToAdd);

    addPropertiesFromRawNodeToDraft(nodeToAdd, draft);
  }
}

function replaceNodeInRawNodesAndUpdatePropertyValuesInDraft(
  draft: WritableDraft<GridState>,
  resultSetId: string,
  nodeToReplace: SemNode
) {
  if (draft.rawNodes[resultSetId] !== undefined) {
    const index = draft.rawNodes[resultSetId].findIndex((n) => n.Id === nodeToReplace.Id);

    if (index !== -1) {
      draft.rawNodes[resultSetId][index] = nodeToReplace;
    } else {
      addRawNodeToDraft(draft, resultSetId, nodeToReplace);
    }
  }

  addPropertiesFromRawNodeToDraft(nodeToReplace, draft);
}

function addPropertiesFromRawNodeToDraft(rawNode: SemNode, draft: WritableDraft<GridState>) {
  const allProperties = getAllProperties(rawNode);

  Object.keys(allProperties).forEach((nodeId) => {
    const existingProperty = draft.properties[nodeId];
    if (existingProperty === undefined) {
      draft.properties[nodeId] = { ...allProperties[nodeId] };
    } else {
      Object.keys(allProperties[nodeId])
        .filter((propertyName) => propertyName !== 'Id')
        .forEach((propertyName) => {
          const newValue = allProperties[nodeId][propertyName];

          // TODO investigate/log, this should not happen. But happens when doing operations on large datasets
          if (existingProperty[propertyName] !== undefined) {
            existingProperty[propertyName] = newValue;
          }
        });
    }
  });
}

function removeNodeInRawNodes(
  state: GridState,
  draft: WritableDraft<GridState>,
  resultSetId: string,
  nodeToRemoveId: string
) {
  if (state.rawNodes[resultSetId] === undefined) {
    return;
  }

  const index = state.rawNodes[resultSetId].findIndex((n) => n.Id === nodeToRemoveId);

  if (index !== -1) {
    draft.rawNodes[resultSetId].splice(index, 1);
  }
}

function getAllProperties(semNode: SemNode) {
  const allNodes = flattenSemNode(semNode);

  const allProperties = allNodes.reduce<Record<string, SemNode['Properties']>>((prev, curr) => {
    prev[curr.Id] = curr.Properties;

    return prev;
  }, {});

  return allProperties;
}

export function flattenSemNode(semNode: SemNode): SemNode[] {
  const childNodes = Object.values(semNode.Edges).flatMap((edges) =>
    edges.flatMap((edge) => flattenSemNode(edge.Target))
  );
  return [semNode, ...childNodes];
}

type ResultSetLayouts = Record<string, CalculatedSemNode[]>;

/**
 * Every recalculation takes a ticket for the result sets it lays out, and only the newest ticket for a
 * result set may commit it. A recalculation reads the rows when it starts but commits after the worker is
 * done, so without this an older one finishing last would put back rows a newer one had replaced
 * (ENG-615: a newly created row shows up, then disappears until a refresh).
 */
const latestRecalculationTickets: Record<string, number> = {};
/** The newest recalculation of each result set, so a recalculation it took over from can wait for it. */
const latestRecalculations: Record<string, Promise<void>> = {};
/**
 * Result sets whose rows changed under a recalculation while the grid loader was still streaming them in.
 * Laying them out again straight away would repeat a full layout after every streamed batch, so it waits
 * until the grid has loaded (see `complete`). Meanwhile the streamed rows show as the loader appends them.
 */
const resultSetsToLayOutOnceLoaded = new Set<string>();
let lastRecalculationTicket = 0;

function takeRecalculationTicket(resultSetIds: string[]) {
  lastRecalculationTicket += 1;
  resultSetIds.forEach((resultSetId) => {
    latestRecalculationTickets[resultSetId] = lastRecalculationTicket;
  });

  return lastRecalculationTicket;
}

/**
 * Drops any recalculation still in flight for these result sets. Call it whenever a result set is reset
 * (cleared or re-initialised): its rows are loaded again, and a layout of the old rows must not land on top.
 */
function cancelInFlightRecalculations(resultSetIds: string[]) {
  takeRecalculationTicket(resultSetIds);
  resultSetIds.forEach((resultSetId) => resultSetsToLayOutOnceLoaded.delete(resultSetId));
}

type SetGridState = (update: (state: GridState) => PartialGridState) => void;

/**
 * Recalculates the layout of the given result sets and commits it. Only those result sets are written,
 * onto the store as it is once the worker is done - never a copy of the whole store taken before the
 * await - so overlapping recalculations of different grids can't undo each other's rows.
 *
 * Resolves only once every given result set shows a layout at least this recent: if a newer
 * recalculation took a result set over, this waits for that one too, so callers (e.g. clearing pending
 * edits) never act on a grid that doesn't show the new rows yet.
 */
async function recalculateResultSetsAndCommit(getState: () => GridState, set: SetGridState, resultSetIds: string[]) {
  const ticket = takeRecalculationTicket(resultSetIds);
  const recalculation = layOutAndCommit(getState, set, resultSetIds, ticket);
  resultSetIds.forEach((resultSetId) => {
    latestRecalculations[resultSetId] = recalculation;
  });

  await recalculation;
  await waitForNewerRecalculations(resultSetIds, recalculation);
}

async function layOutAndCommit(getState: () => GridState, set: SetGridState, resultSetIds: string[], ticket: number) {
  const isLatest = (resultSetId: string) => latestRecalculationTickets[resultSetId] === ticket;
  let resultSetsToLayOut = resultSetIds;

  while (resultSetsToLayOut.length > 0) {
    const before = getState();
    const layouts = await calculateResultSetLayouts(before, resultSetsToLayOut);
    const after = getState();

    // Result sets a newer recalculation took over are left to it. Rows that changed while they were laid
    // out are laid out again rather than committed stale - right away, or once the grid has loaded if the
    // grid loader is still streaming rows into it.
    const isUnchanged = (resultSetId: string) => after.rawNodes[resultSetId] === before.rawNodes[resultSetId];
    const isLoading = (resultSetId: string) => after.completedResultSets[resultSetId] !== true;
    const currentLayouts: ResultSetLayouts = Object.fromEntries(
      Object.entries(layouts).filter(([resultSetId]) => isLatest(resultSetId) && isUnchanged(resultSetId))
    );
    const changedResultSets = Object.keys(layouts).filter(
      (resultSetId) => isLatest(resultSetId) && !isUnchanged(resultSetId)
    );
    changedResultSets.filter(isLoading).forEach((resultSetId) => resultSetsToLayOutOnceLoaded.add(resultSetId));
    resultSetsToLayOut = changedResultSets.filter((resultSetId) => !isLoading(resultSetId));

    if (Object.keys(currentLayouts).length > 0) {
      set((state) => applyResultSetLayouts(pickState(state), currentLayouts));
    }
  }
}

async function waitForNewerRecalculations(resultSetIds: string[], ownRecalculation: Promise<void>) {
  const waitedFor = new Set([ownRecalculation]);
  const findNewer = () =>
    uniq(resultSetIds.map((resultSetId) => latestRecalculations[resultSetId])).filter(
      (recalculation): recalculation is Promise<void> => recalculation !== undefined && !waitedFor.has(recalculation)
    );

  let newer = findNewer();
  while (newer.length > 0) {
    newer.forEach((recalculation) => waitedFor.add(recalculation));
    // A newer recalculation failing is its own caller's error to handle, not this one's.
    await Promise.allSettled(newer);
    newer = findNewer();
  }
}

async function calculateResultSetLayouts(state: PartialGridState, resultSetIds: string[]) {
  const layouts: ResultSetLayouts = {};

  for (const resultSetId of resultSetIds) {
    const nodes = state.rawNodes[resultSetId];
    const gridSpec = state.gridSpecifications[resultSetId];

    if (!nodes || !gridSpec) {
      continue;
    }

    // Note: the payload needs to be serializable for the worker
    // So we need to use state, instead of draft here.
    layouts[resultSetId] = await calculateNodesAsync({
      nodes,
      columnWidthMap: gridSpec.columnWidthsByKey,
      edgeKeyMap: gridSpec.edgeKeyMap,
      propertyKeyMap: gridSpec.propertyKeyMap,
      columnKey: 'root',
      propertyUpdates: []
    });
  }

  return layouts;
}

function applyResultSetLayouts(state: PartialGridState, layouts: ResultSetLayouts) {
  const resultSetIds = Object.keys(layouts);

  // Capture current selections before recalculation
  // nodeMatrix will be rebuilt, so we need to capture the current selections and restore it later.
  // Otherwise, we will lose selection state.
  const capturedSelections: CapturedSelections = captureCurrentSelections(state, resultSetIds);

  let updatedState = state;

  for (const resultSetId of resultSetIds) {
    const newCalculatedNodes = layouts[resultSetId];
    const gridSpec = updatedState.gridSpecifications[resultSetId];

    if (!gridSpec) {
      continue;
    }

    updatedState = produce(updatedState, (draft) => {
      const renderedRowStart = GRID_HEADER_ROW_COUNT;
      const rowIndexedNodes = addRowStartAndColumnIndex(
        newCalculatedNodes,
        renderedRowStart,
        gridSpec.columnIndexByKey,
        gridSpec.edgeKeyMap,
        'root'
      );
      const rowNumber = 1;
      const indexedCalculatedNewNodes = setOriginalRowIndex(rowIndexedNodes, rowNumber, gridSpec.edgeKeyMap, 'root');
      const flatResult = deepFlattenNodes(indexedCalculatedNewNodes);

      const isSpliced: Record<number, boolean> = {};

      if (flatResult.length > 0) {
        flatResult.forEach((item) => {
          const column = draft.nodeMatrix[resultSetId][item.column];

          if (!isSpliced[item.column]) {
            column.splice(0);
            isSpliced[item.column] = true;
          }

          column[item.rowStart] = item;
        });
      }

      Object.keys(gridSpec.columnSpecificationByIndexMap).forEach((columnIndex) => {
        const columnIndexAsNumber = Number.parseInt(columnIndex);
        if (!isSpliced[columnIndexAsNumber]) {
          const column = draft.nodeMatrix[resultSetId][columnIndexAsNumber];
          column.splice(0);
          isSpliced[columnIndexAsNumber] = true;
        }
      });

      draft.rootNodes[resultSetId] = {};
      indexedCalculatedNewNodes.forEach((i) => {
        draft.rootNodes[resultSetId][i.id] = i;
      });

      const rootNodes = indexedCalculatedNewNodes;

      const lastRootNode = rootNodes[rootNodes.length - 1];
      const rowCount = rootNodes.length === 0 ? renderedRowStart : lastRootNode.rowNumber;

      draft.rowCounts[resultSetId] = rowCount;
      draft.nodeMatrixRowCount[resultSetId] =
        lastRootNode !== undefined ? lastRootNode.rowStart + lastRootNode.lineCount : 0;

      const rowsPerRootNode = rootNodes.reduce<{ rowStart: number; rowEnd: number }[]>((previous, node) => {
        const previousRowNumber = previous.length === 0 ? node.rowStart : previous[previous.length - 1].rowEnd;
        previous.push({ rowStart: previousRowNumber, rowEnd: previousRowNumber + node.lineCount });
        return previous;
      }, []);

      // Rebuilt from scratch: rows that are gone (or got shorter) must not keep their old mapping.
      draft.rootRowMap[resultSetId] = {};
      rowsPerRootNode.reduce<Record<number, number>>((prev, current, index) => {
        const start = current.rowStart;
        const end = current.rowEnd - 1;
        const rowsForThisNode = Array(end - start + 1)
          .fill(start)
          .map((x, y) => x + y);
        rowsForThisNode.forEach((rowIndex) => {
          prev[rowIndex] = index + rowNumber;

          draft.rootRowMap[resultSetId][rowIndex] = index + rowNumber; // <-- draft set here
        });

        return prev;
      }, {});
    });
  }

  // Restore selections using captured references
  updatedState = produce(updatedState, (draft) => {
    const restoredState = restoreCapturedSelections(draft, capturedSelections, resultSetIds);
    draft.selectedCells = restoredState.selectedCells;
    draft.editCells = restoredState.editCells;
  });

  return updatedState;
}

// Only update part of the grid state to prevent unnecessary re-renders.
type PartialGridState = Pick<
  GridState,
  | 'gridSpecifications'
  | 'nodeMatrix'
  | 'rootNodes'
  | 'rowCounts'
  | 'nodeMatrixRowCount'
  | 'rootRowMap'
  | 'rawNodes'
  | 'selectedCells'
  | 'editCells'
>;

function pickState(state: GridState): PartialGridState {
  return pick(
    state,
    'gridSpecifications',
    'nodeMatrix',
    'rootNodes',
    'rowCounts',
    'nodeMatrixRowCount',
    'rawNodes',
    'rootRowMap',
    'selectedCells',
    'editCells'
  );
}

export function isPropertyEditable(propertyId: string, accessRightsMap: AccessRightsMap) {
  const rightsMap = accessRightsMap[propertyId];

  if (!rightsMap) {
    return false;
  }

  return Object.values(rightsMap).some((value) => value === EOverrideAccessRights.edit);
}

export function createFilterFromQueryInitialization(queryInitialization: QueryInitialization) {
  const { specification, gridSpecification } = queryInitialization;

  const result = Object.keys(specification.columns).reduce<Record<string, ColumnFilter>>((record, columnKey) => {
    if (hasValue(gridSpecification.propertyKeyMap[columnKey])) {
      record[columnKey] = { keys: [], allowBlanks: EAllowBlanks.NotSet };
    }
    return record;
  }, {});

  return result;
}

export function createPropertyFilterFromQueryInitialization(queryInitialization: QueryInitialization) {
  const result = Object.entries(queryInitialization.specification.columns).reduce<Record<string, PropertyFilter>>(
    (record, [columnKey, targetColumn]) => {
      if (!targetColumn.isSemanticEdit) {
        const propertyType = targetColumn.propertyTraversal?.propertyType;

        if (propertyType) {
          record[columnKey] = {
            values: [],
            allowBlanks: EAllowBlanks.NotSet,
            propertyType: propertyType
          };
        }

        return record;
      } else {
        return record;
      }
    },
    {}
  );

  return result;
}
